import { useMemo, useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, MoreHorizontal, Trash2, Edit2, Check, X, AlertTriangle, Focus } from "lucide-react";
import clsx from "clsx";
import type { Card, Lane } from "../../types";
import { CardView } from "./CardView";
import { useStore } from "../../stores/store";
import { api } from "../../lib/api";
import { confirmAsync, promptAsync } from "../../stores/dialogStore";

interface Props {
  lane: Lane;
  cards: Card[];
  onCardClick: (id: number) => void;
  onQuickAdd: (laneId: number) => void;
  focused: boolean;
}

export function LaneColumn({ lane, cards, onCardClick, onQuickAdd, focused }: Props) {
  const droppableData = useMemo(
    () => ({ type: "lane" as const, laneId: lane.id }),
    [lane.id],
  );
  const { setNodeRef, isOver } = useDroppable({ id: `lane-${lane.id}`, data: droppableData });
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(lane.title);
  const upsertLane = useStore((s) => s.upsertLane);
  const removeLane = useStore((s) => s.removeLane);
  const setFocusLane = useStore((s) => s.setFocusLane);
  const focusLaneId = useStore((s) => s.focusLaneId);

  const overLimit = lane.wipLimit != null && cards.length > lane.wipLimit;

  async function saveTitle() {
    if (!title.trim() || title === lane.title) {
      setEditing(false);
      setTitle(lane.title);
      return;
    }
    const updated = await api.updateLane(lane.id, { title: title.trim() });
    upsertLane(updated);
    setEditing(false);
  }

  async function deleteLane() {
    const ok = await confirmAsync({
      title: `Remover a raia "${lane.title}"?`,
      description: "Os cards nela serão removidos permanentemente. Esta ação não pode ser desfeita.",
      confirmLabel: "Remover",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteLane(lane.id);
      removeLane(lane.id);
    } catch (e: any) {
      await confirmAsync({ title: "Erro", description: e.message, confirmLabel: "OK", cancelLabel: "" });
    }
  }

  async function setWipLimit() {
    setMenuOpen(false);
    const raw = await promptAsync({
      title: `WIP limit para "${lane.title}"`,
      description: "Deixe em branco para remover o limite.",
      placeholder: "Ex: 3",
      defaultValue: lane.wipLimit?.toString() ?? "",
      confirmLabel: "Aplicar",
      validate: (v) => {
        if (v.trim() === "") return null;
        const n = parseInt(v, 10);
        if (isNaN(n) || n < 0) return "Digite um número positivo ou deixe em branco";
        return null;
      },
    });
    if (raw === null) return;
    const n = raw.trim() === "" ? null : parseInt(raw, 10);
    const updated = await api.updateLane(lane.id, { wipLimit: n });
    upsertLane(updated);
  }

  return (
    <div className={clsx("flex shrink-0 flex-col snap-start md:snap-align-none", focused ? "w-full" : "w-[calc(100vw-1.5rem)] md:w-[300px]")}>
      <div
        className="pointer-events-none mx-1 h-px rounded-full"
        style={{ background: `linear-gradient(90deg, transparent, ${lane.color}55, transparent)` }}
      />
      <div className="flex items-center justify-between px-2 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="h-2 w-2 rounded-full shadow-[0_0_8px_currentColor]"
            style={{ backgroundColor: lane.color, color: lane.color }}
          />
          {editing ? (
            <div className="flex items-center gap-1">
              <input
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveTitle();
                  if (e.key === "Escape") {
                    setEditing(false);
                    setTitle(lane.title);
                  }
                }}
                className="w-28 rounded bg-white/5 px-2 py-0.5 text-sm font-semibold text-slate-100 outline-none ring-1 ring-violet-500/40 focus:ring-violet-500"
              />
              <button onClick={saveTitle} className="rounded p-1 text-emerald-300 hover:bg-white/5">
                <Check className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => {
                  setEditing(false);
                  setTitle(lane.title);
                }}
                className="rounded p-1 text-slate-400 hover:bg-white/5"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setFocusLane(focusLaneId === lane.id ? null : lane.id)}
              className="truncate text-sm font-semibold uppercase tracking-wide text-slate-200 hover:text-violet-200"
              title="Clique para entrar/sair do modo foco"
            >
              {lane.title}
            </button>
          )}
          <span
            className={clsx(
              "rounded-md px-1.5 py-0.5 font-mono text-[10px]",
              overLimit ? "animate-pulse bg-rose-500/25 text-rose-200 ring-1 ring-rose-500/50" : "bg-white/5 text-slate-400",
            )}
          >
            {cards.length}
            {lane.wipLimit != null ? `/${lane.wipLimit}` : ""}
          </span>
          {overLimit && <AlertTriangle className="h-3 w-3 text-rose-300" />}
        </div>
        <div className="relative flex items-center gap-0.5">
          <button
            onClick={() => onQuickAdd(lane.id)}
            className="rounded p-1 text-slate-400 transition hover:bg-white/5 hover:text-slate-100"
            title="Novo card aqui"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="rounded p-1 text-slate-400 transition hover:bg-white/5 hover:text-slate-100"
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="glass absolute right-0 top-7 z-10 w-48 overflow-hidden rounded-lg p-1 text-sm shadow-xl"
                onMouseLeave={() => setMenuOpen(false)}
              >
                <button
                  onClick={() => {
                    setEditing(true);
                    setMenuOpen(false);
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-slate-200 hover:bg-white/5"
                >
                  <Edit2 className="h-3.5 w-3.5" /> Renomear
                </button>
                <button
                  onClick={() => {
                    setFocusLane(focusLaneId === lane.id ? null : lane.id);
                    setMenuOpen(false);
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-slate-200 hover:bg-white/5"
                >
                  <Focus className="h-3.5 w-3.5" /> {focusLaneId === lane.id ? "Sair do foco" : "Modo foco"}
                </button>
                <button
                  onClick={setWipLimit}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-slate-200 hover:bg-white/5"
                >
                  <AlertTriangle className="h-3.5 w-3.5" /> WIP limit
                </button>
                <button
                  onClick={deleteLane}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-rose-300 hover:bg-rose-500/10"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Remover raia
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div
        ref={setNodeRef}
        className={clsx(
          "relative flex min-h-[120px] flex-1 flex-col gap-2 scrollbar-thin rounded-xl p-2 transition-all duration-200 ease-soft overflow-y-auto",
          "bg-white/[0.015] ring-1 ring-inset ring-white/5 backdrop-blur-[2px]",
          isOver && "ring-violet-400/60 bg-violet-500/[0.07] shadow-[inset_0_0_40px_rgba(168,85,247,0.12)]",
          overLimit && "ring-rose-500/40 bg-rose-500/[0.03]",
        )}
        style={{
          backgroundImage: isOver
            ? undefined
            : `linear-gradient(180deg, ${lane.color}0a 0%, transparent 120px)`,
        }}
      >
        <SortableContext items={cards.map((c) => c.id)} strategy={verticalListSortingStrategy}>
          {cards.map((c) => (
            <CardView key={c.id} card={c} onClick={() => onCardClick(c.id)} />
          ))}
        </SortableContext>

        <button
          onClick={() => onQuickAdd(lane.id)}
          className={clsx(
            "mt-1 flex shrink-0 items-center justify-center gap-1 rounded-lg border border-dashed transition",
            cards.length === 0
              ? "border-white/10 px-3 py-3 text-[11px] text-slate-500 hover:border-violet-400/40 hover:text-slate-300"
              : "border-white/5 px-2 py-1.5 text-[10px] text-slate-500/70 hover:border-violet-400/30 hover:text-slate-300",
          )}
        >
          <Plus className="h-3 w-3" /> adicionar card
        </button>
      </div>
    </div>
  );
}
